import React from 'react';
import { usePushNotifications } from '../../hooks/usePushNotifications';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { Bell, BellOff, BellRing, Loader2, AlertTriangle, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const PushNotificationSettings = ({ variant = 'card' }) => {
  const {
    isSupported,
    permission,
    isSubscribed,
    isLoading,
    error,
    toggle
  } = usePushNotifications();

  const handleToggle = async () => {
    const result = await toggle();
    
    if (result.success) {
      if (isSubscribed) {
        toast.success('Push notifications disabled');
      } else {
        toast.success('Push notifications enabled! You will receive alerts even when the app is closed.');
      }
    } else {
      if (result.error === 'Permission denied') {
        toast.error('Please allow notifications in your browser settings');
      } else {
        toast.error(result.error || 'Failed to update notification settings');
      }
    }
  };

  // Compact inline variant
  if (variant === 'inline') {
    if (!isSupported) {
      return null;
    }

    return (
      <div className="flex items-center justify-between gap-4 p-3 rounded-lg bg-zinc-800/50 border border-zinc-700">
        <div className="flex items-center gap-3">
          {isSubscribed ? (
            <BellRing className="w-5 h-5 text-green-400" />
          ) : (
            <BellOff className="w-5 h-5 text-zinc-500" />
          )}
          <div>
            <p className="text-sm font-medium text-white">Push Notifications</p>
            <p className="text-xs text-zinc-500">
              {isSubscribed ? 'Enabled - alerts when app is closed' : 'Enable to receive alerts'}
            </p>
          </div>
        </div>
        <Switch
          checked={isSubscribed}
          onCheckedChange={handleToggle}
          disabled={isLoading || permission === 'denied'}
          data-testid="push-toggle-inline"
        />
      </div>
    );
  }

  // Full card variant
  return (
    <Card className="bg-zinc-900 border-zinc-800">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${isSubscribed ? 'bg-green-500/20' : 'bg-zinc-700'}`}>
              {isSubscribed ? (
                <BellRing className="w-5 h-5 text-green-400" />
              ) : (
                <Bell className="w-5 h-5 text-zinc-400" />
              )}
            </div>
            <div>
              <CardTitle className="text-lg text-white">Push Notifications</CardTitle>
              <CardDescription className="text-zinc-400">
                Receive alerts even when the app is closed
              </CardDescription>
            </div>
          </div>
          {isSubscribed && (
            <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
              <CheckCircle className="w-3 h-3 mr-1" />
              Enabled
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {!isSupported ? (
          <div className="flex items-center gap-3 p-3 rounded-lg bg-orange-500/10 border border-orange-500/30">
            <AlertTriangle className="w-5 h-5 text-orange-400 flex-shrink-0" />
            <p className="text-sm text-orange-300">
              Push notifications are not supported in your browser. Try using Chrome, Firefox, or Edge.
            </p>
          </div>
        ) : permission === 'denied' ? (
          <div className="flex items-center gap-3 p-3 rounded-lg bg-red-500/10 border border-red-500/30">
            <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0" />
            <div>
              <p className="text-sm text-red-300 font-medium">Notifications Blocked</p>
              <p className="text-xs text-red-400 mt-1">
                Please enable notifications in your browser settings to receive alerts.
              </p>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between">
              <Label htmlFor="push-toggle" className="text-zinc-300">
                Enable Push Notifications
              </Label>
              <Switch
                id="push-toggle"
                checked={isSubscribed}
                onCheckedChange={handleToggle}
                disabled={isLoading}
                data-testid="push-toggle"
              />
            </div>

            {isSubscribed ? (
              <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/30">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-green-300 font-medium">Notifications Active</p>
                    <p className="text-xs text-green-400 mt-1">
                      You will receive emergency alerts even when this app is closed or your browser is minimized.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-3 rounded-lg bg-zinc-800/50 border border-zinc-700">
                <p className="text-sm text-zinc-400">
                  Enable push notifications to receive critical emergency alerts instantly, even when you're not using the app.
                </p>
              </div>
            )}

            {isLoading && (
              <div className="flex items-center justify-center py-2">
                <Loader2 className="w-5 h-5 animate-spin text-zinc-400" />
                <span className="ml-2 text-sm text-zinc-400">Updating...</span>
              </div>
            )}

            {error && (
              <p className="text-sm text-red-400">{error}</p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default PushNotificationSettings;
